/**
 * R5 — Annotations. Operator's working memory. Per CD spec:
 *   - Markdown body (subset: **bold**, _italic_, - bullet).
 *   - LOCAL-ONLY lock (🔒) unmissable on every annotation — annotations
 *     may carry sensitive content (e.g. "source X confided that..."),
 *     never shared outside this tenant.
 *   - Decay flag when untouched > N days (CD spec: 7d threshold) —
 *     "untouched Nd ago" instead of "edited Nd ago" + visual demotion.
 */

import { useState } from 'react';
import { FamilyGlyph, LocalOnlyLock, relTime } from './primitives';
import { FAM, IDC } from './sample-data';
import type { Annotation, PathogenFamily } from './types';

export interface AnnotationsProps {
  annotations: readonly Annotation[];
}

/** Render a markdown subset (bold / italic / bullet / paragraph) to JSX
 *  without external dependencies. Sanitisation strategy: only known
 *  patterns produce HTML; everything else renders as plain text. */
function markdownToBlocks(body: string): React.ReactNode {
  const lines = body.split('\n');
  const out: React.ReactNode[] = [];
  let listBuf: string[] | null = null;

  const flushList = () => {
    if (listBuf && listBuf.length) {
      out.push(
        <ul key={`ul${out.length}`} style={{ margin: '4px 0 6px 18px', padding: 0 }}>
          {listBuf.map((line, i) => (
            <li key={i} style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--bm-text)' }}>
              {renderInline(line)}
            </li>
          ))}
        </ul>,
      );
    }
    listBuf = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.startsWith('- ')) {
      if (!listBuf) listBuf = [];
      listBuf.push(line.slice(2));
    } else if (!line.trim()) {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={`p${i}`} style={{ margin: '4px 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text)' }}>
          {renderInline(line)}
        </p>,
      );
    }
  }
  flushList();
  return out;
}

/** Inline-format **bold** and _italic_ — segmented render so the spans
 *  are real React elements (no dangerouslySetInnerHTML, no sanitisation
 *  headache). */
function renderInline(text: string): React.ReactNode {
  // Tokenise into bold | italic | plain segments via regex.
  const parts: Array<{ kind: 'plain' | 'bold' | 'italic'; text: string }> = [];
  const re = /\*\*(.+?)\*\*|_(.+?)_/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: 'plain', text: text.slice(last, m.index) });
    if (m[1] !== undefined) parts.push({ kind: 'bold', text: m[1] });
    else if (m[2] !== undefined) parts.push({ kind: 'italic', text: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ kind: 'plain', text: text.slice(last) });

  return parts.map((p, i) => {
    if (p.kind === 'bold') return <b key={i}>{p.text}</b>;
    if (p.kind === 'italic') return <i key={i}>{p.text}</i>;
    return <span key={i}>{p.text}</span>;
  });
}

export function Annotations({ annotations: initial }: AnnotationsProps) {
  const [annotations] = useState<readonly Annotation[]>(initial);

  return (
    <div>
      <header style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 680 }}>Annotations</h2>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, color: 'var(--bm-text-subtle)',
          marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          your working memory · markdown <LocalOnlyLock />
        </div>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {annotations.map(n => {
          const family = (FAM[n.pathogen] ?? 'respiratory') as PathogenFamily;
          const color = IDC[n.pathogen] ?? 'var(--bm-text-muted)';
          return (
            <article
              key={n.id}
              style={{
                padding: 12,
                background: n.decayed ? 'var(--bm-bg)' : 'var(--bm-bg-subtle)',
                border: '1px solid var(--bm-border)',
                borderLeft: `3px solid ${n.decayed ? 'var(--bm-text-subtle)' : 'var(--bm-anno)'}`,
                borderRadius: 'var(--bm-radius-sm)',
                opacity: n.decayed ? 0.75 : 1,
              }}
            >
              <header style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, marginBottom: 8,
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }}>
                  <FamilyGlyph family={family} color={color} size={13} />
                  {n.target}
                </span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
                  color: n.decayed ? 'var(--bm-threat-3)' : 'var(--bm-text-subtle)',
                }}>
                  {n.decayed
                    ? `⚠ untouched ${n.touchedDays}d`
                    : `edited ${relTime(n.edited)}`}
                </span>
              </header>

              <div style={{ marginBottom: 8 }}>
                {markdownToBlocks(n.body)}
              </div>

              <footer style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LocalOnlyLock small />
                <button
                  type="button"
                  style={{
                    marginLeft: 'auto', padding: '3px 8px', borderRadius: 4,
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
                    color: 'var(--bm-text-muted)',
                    background: 'transparent',
                    border: '1px solid var(--bm-border)',
                    cursor: 'pointer',
                  }}
                >Edit</button>
              </footer>
            </article>
          );
        })}
      </div>
    </div>
  );
}
